import { useEffect, useState } from "react";

import { BookingModal } from "@/components/landing/BookingModal";
import { campaignTags } from "@/lib/campaign";
import { recordContactTap } from "@/lib/contact-tap.functions";
import { trackPixel } from "@/lib/meta-pixel";

type Opening = "form" | "connect";

const HASHES: Record<string, Opening> = { "#audit": "form", "#connect": "connect" };

/** Pages reached after a sale. Their WhatsApp taps still alert Kristian. */
const AFTER_SALE_PATHS = ["/start", "/access"];

/**
 * One review dialog for the whole site, opened over whatever page the
 * visitor is reading.
 *
 * Every "Request a review" button used to send people somewhere else first:
 * to /meta-ads-audit, whose own button then sent them to the homepage, which
 * was the only page with the dialog. Two page loads to reach four fields, and
 * the visitor lost the page they had been reading. Now any same-site link to
 * #audit — including the /#audit links written before this — opens the dialog
 * in place.
 *
 * #connect opens it on the Meta connection path, which is otherwise hidden
 * until the app is published; see META_CONNECT_PUBLIC in BookingModal.
 */
export function BookingDialogHost() {
  const [open, setOpen] = useState(false);
  // Kept after closing, so the dialog does not switch panels while it fades.
  const [path, setPath] = useState<Opening>("form");

  useEffect(() => {
    // Read the campaign tags now, while the address still carries them: the
    // first in-site navigation drops the query string.
    campaignTags();

    const show = (target: Opening) => {
      setPath(target);
      setOpen(true);
    };

    // Arriving with the hash already set — from an email, a social profile,
    // the App Review instructions — or navigating back to it.
    const fromHash = () => {
      const target = HASHES[window.location.hash];
      if (target) show(target);
    };
    // Not immediately. The page under the dialog is a lazily loaded route that
    // hydrates after this effect runs; opening now lets Radix mark that
    // not-yet-hydrated markup aria-hidden, and React then finds attributes it
    // never rendered. A short wait after the load event puts the opening after
    // hydration. A timer rather than an animation frame: frames do not run in
    // a background tab, and a link opened with cmd-click lands in one.
    let timer = 0;
    const openFromAddress = () => {
      timer = window.setTimeout(fromHash, 150);
    };
    if (document.readyState === "complete") openFromAddress();
    else window.addEventListener("load", openFromAddress, { once: true });
    window.addEventListener("hashchange", fromHash);

    // Intercepted rather than left to the browser: a plain hash link would
    // also scroll the page to #audit, and from any page but the homepage an
    // /#audit link would navigate away first.
    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = (event.target as Element | null)?.closest?.("a[href]");
      if (!(link instanceof HTMLAnchorElement)) return;
      const url = new URL(link.href, window.location.href);
      // WhatsApp opens in a new tab and leaves the site, so the intent is
      // recorded here or not at all — and before the new-tab return below.
      // Until 29 Sep this sat after that return and never fired.
      if (url.hostname === "wa.me") {
        // Meta's standard Contact event, so Ads Manager can count it and a
        // campaign can optimise for it. A no-op without measurement consent.
        // Not on the pages for people who have already paid, or their admin:
        // a client asking for help is not a new enquiry, and counting one
        // would teach the campaign to look for more of the wrong tap.
        if (!AFTER_SALE_PATHS.includes(window.location.pathname)) {
          trackPixel("Contact", { content_name: "whatsapp" });
        }
        // The site's own record, which needs no consent because it holds
        // nothing personal. Never awaited: WhatsApp is already opening.
        void recordContactTap({
          data: { channel: "whatsapp", page: window.location.pathname, campaign: campaignTags() },
        }).catch(() => {});
      }
      if (link.target === "_blank") return;
      const target = HASHES[url.hash];
      if (!target || url.origin !== window.location.origin) return;
      event.preventDefault();
      show(target);
    };
    document.addEventListener("click", onClick);

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("load", openFromAddress);
      window.removeEventListener("hashchange", fromHash);
      document.removeEventListener("click", onClick);
    };
  }, []);

  const onOpenChange = (value: boolean) => {
    setOpen(value);
    // Leave the address clean, so the same link opens it again and a reload
    // does not reopen it.
    if (!value && HASHES[window.location.hash]) {
      history.replaceState(history.state, "", window.location.pathname + window.location.search);
    }
  };

  return <BookingModal open={open} onOpenChange={onOpenChange} initialPath={path} />;
}
